/* eslint-disable no-console */
import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
// import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {CheckboxField} from 'helpers'
import {Select2DropdownField} from 'helpers/ui-components/form/Select2DropdownField'
import IDropdown from 'models/dropdown'
import {ColumnProps, IFormField} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {
 fetchDistrict,
 fetchState,
} from 'utils/api/auth/locationService'
import {IUser} from 'models/UserManagement'
import {
 fetchAssignedSubDistrictList,
 createGeoMapping,
 fetchAssignedDistrictList,
 fetchAssignedStateList,
} from 'utils/api/auth/userManagementService'
import {toast} from 'react-toastify'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {Link} from 'react-router-dom'
// import {ILogin} from 'models/Auth'
type IProps = {
 userId: string
 userRole: string
 activeTabId: number
 loggedInUserId: number | undefined
 setLoader: (newState: boolean) => void
 handleTabClick: (
  newState: number,
  isBackBtnClick: boolean
 ) => void
}

type ITableData = {
 users: IUser[]
 id: string
 name: string
 is_active?: boolean
}

type GeoLevel =
 | 'subDistrict'
 | 'district'
 | 'state'
 | 'rbmState'

const getGeoLevel = (userRole: string): GeoLevel => {
 if (['dl', 'mlc'].includes(userRole)) {
  return 'subDistrict'
 }
 if (
  ['sr_dl', 'sr_mlc_fv', 'sr_mlc_dry'].includes(userRole)
 ) {
  return 'district'
 }
 if (userRole === 'rbm') {
  return 'rbmState'
 }
 return 'state'
}

type GeoLevelConfig = {
 showStateDropdown: boolean
 showDistrictDropdown: boolean
 showLoadButton: boolean
 autoLoadAssignedList: boolean
 usesSelectedIdsAsStates: boolean
 labelKey: 'state' | 'district' | 'subDistrict'
}

const GEO_LEVEL_CONFIG: Record<GeoLevel, GeoLevelConfig> = {
 state: {
  showStateDropdown: true,
  showDistrictDropdown: false,
  showLoadButton: false,
  autoLoadAssignedList: false,
  usesSelectedIdsAsStates: false,
  labelKey: 'state',
 },
 district: {
  showStateDropdown: true,
  showDistrictDropdown: false,
  showLoadButton: true,
  autoLoadAssignedList: false,
  usesSelectedIdsAsStates: false,
  labelKey: 'district',
 },
 subDistrict: {
  showStateDropdown: true,
  showDistrictDropdown: true,
  showLoadButton: true,
  autoLoadAssignedList: false,
  usesSelectedIdsAsStates: false,
  labelKey: 'subDistrict',
 },
 rbmState: {
  showStateDropdown: false,
  showDistrictDropdown: false,
  showLoadButton: false,
  autoLoadAssignedList: true,
  usesSelectedIdsAsStates: true,
  labelKey: 'state',
 },
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GeographyMapping = (props: IProps) => {
 const {t} = useTranslation()
 const isDisabled = true
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<ITableData>>
 >([])
 const [searchTerm, setSearchTerm] = useState('')
 const [stateList, setStateList] = useState<IDropdown[]>([])
 const [districtList, setDistrictList] = useState<
  IDropdown[]
 >([])
 const [tableDataList, setTableDataList] = useState<
  ITableData[]
 >([])
 const [tableRows, setTableRows] = useState<ITableData[]>(
  []
 )
 const level = getGeoLevel(props.userRole)
 const levelConfig = GEO_LEVEL_CONFIG[level]
 const getLevelLabel = () =>
  t(
   `userManagement.activate.geoMapping.${levelConfig.labelKey}`
  )
 const getLevelNameColumnLabel = () =>
  t(
   `userManagement.activate.geoMapping.${levelConfig.labelKey}Name`
  )
 const {
  register,
  control,
  getValues,
  watch,
  setValue,
  handleSubmit,
  formState: {errors},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
 })

 async function submitFormData(formData: IFormField) {
  if (
   formData?.selectedIds?.length > 0 ||
   level === 'state'
  ) {
   props.setLoader(true)
   const payload = {
    user_id: props.userId,
    states: levelConfig.usesSelectedIdsAsStates
     ? formData.selectedIds
     : [formData.state],
    ...(['subDistrict', 'district'].includes(level) && {
     districts:
      level === 'subDistrict'
       ? [formData.district]
       : formData.selectedIds,
    }),
    ...(level === 'subDistrict' && {
     subdistricts: formData.selectedIds,
    }),
   }
   const res = await createGeoMapping(payload)
   if (res) {
    toast.success(res)
   }
   props.setLoader(false)
   props.handleTabClick(props.activeTabId, false)
  } else {
   toast.error(t('errors.pleaseSelectDistrict'))
  }
 }

 const getDistrict = async (stateId: number) => {
  setValue('district', '')
  setDistrictList([])
  setTableDataList([])
  if (stateId) {
   if (level !== 'state') {
    props.setLoader(true)
    const res: IDropdown[] = await fetchDistrict(stateId)
    if (res) {
     setDistrictList(res)
    }
    props.setLoader(false)
   }
  }
 }

 const renderTableData = async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<ITableData>> = [
   {
    key: 'name',
    title: getLevelNameColumnLabel(),
   },
   {
    key: 'currentlyAssignedTo',
    title: t(
     'userManagement.activate.geoMapping.currentlyAssignedTo'
    ),
    render: (_, record) => {
     return record?.users?.[0] ? (
      <Link
       target='_blank'
       rel='noreferrer'
       className='w-full text-[#4E83E4] underline'
       to={`/user-details/${record?.users[0]?.id}`}
      >
       {record?.users[0]?.full_name}
      </Link>
     ) : (
      <span className='text-[#C5CED6]'>
       {t('userManagement.activate.geoMapping.notAssigned')}
      </span>
     )
    },
   },
   {
    key: 'action',
    title: t('userManagement.activate.geoMapping.action'),
    className: '!text-center',
    render: (_, record) => {
     const disabled =
      record.users.length > 0
       ? record.users[0]?.id !== Number(props.userId)
       : false
     const isAdded =
      disabled ||
      (watch(`selectedIds`).length > 0 &&
       watch(`selectedIds`)?.includes(record.id.toString()))
     return (
      <div
       key={record.id}
       className='w-full flex justify-center items-center'
      >
       <div
        className={`${
         disabled && 'text-[#C5CED6] border-[#E6EBF0]'
        } flex justify-center items-center w-[118px] h-12 rounded-lg border border-[#4E83E4] cursor-pointer text-[#4E83E4] font-semibold text-center
    shadow-[0_4px_4px_0_rgba(0, 0, 0, 0.12)] bg-white px-1 py-[3px]`}
       >
        <CheckboxField
         type='checkbox'
         disabled={disabled}
         checked={isAdded}
         label={''}
         className={`activate-user-checkbox w-5 h-5 mt-1 text-blue-600 bg-gray-100 rounded border-gray-300 border-neutral-30`}
         wrapperClassName=''
         value={record.id.toString()}
         name={`selectedIds`}
         id={`selectedIds-${record.id}`}
         register={register(`selectedIds`, {
          required: false,
         })}
        />
        <span
         className={`${
          disabled && 'text-[#C5CED6]'
         } text-base font-semibold ml-1`}
        >
         {isAdded
          ? t('userManagement.activate.geoMapping.added')
          : t('userManagement.activate.geoMapping.add')}
        </span>
       </div>
      </div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }

 const loadRbmStateTableData = async () => {
  props.setLoader(true)
  const data: ITableData[] = await fetchAssignedStateList(
   props.userRole
  )
  if (data) {
   const assigendForOwn = data
    .filter(
     (val) =>
      val?.users &&
      val.users.some(
       (user) => user.id === Number(props.userId)
      )
    )
    .map((item) => item.id.toString())
   setValue('selectedIds', assigendForOwn)
   setTableDataList(data)
   setTableRows(data)
  }
  props.setLoader(false)
  await renderTableData()
 }

 const loadTableData = async () => {
  props.setLoader(true)
  let assigendForOwn: string[] = []
  let data: ITableData[]
  if (level === 'subDistrict') {
   const districtId = parseInt(getValues('district'))
   data = await fetchAssignedSubDistrictList(
    districtId,
    props.userRole
   )
  } else {
   const stateId = parseInt(getValues('state'))
   data = await fetchAssignedDistrictList(
    stateId,
    props.userRole
   )
  }
  if (data) {
   assigendForOwn = data
    .filter(
     (val) =>
      val?.users &&
      val.users.some(
       (user) => user.id === Number(props.userId)
      )
    )
    .map((item) => item.id.toString())
   setValue('selectedIds', assigendForOwn)
   setTableDataList(data)
   setTableRows(data)
  }
  props.setLoader(false)
  await renderTableData()
 }

 const handleSearch = (value: string) => {
  if (value) {
   const temp = tableDataList.filter((item) =>
    item.name.toLowerCase().includes(value.toLowerCase())
   )
   setTableRows(temp)
  } else {
   setTableRows(tableDataList)
  }
 }

 useEffect(() => {
  if (!levelConfig.showStateDropdown) {
   return
  }
  const getState = async () => {
   props.setLoader(true)
   const res: IDropdown[] = await fetchState()
   if (res) {
    setStateList(res)
   }
   props.setLoader(false)
  }
  void getState()
 }, [levelConfig.showStateDropdown, props.userRole])

 useEffect(() => {
  if (levelConfig.autoLoadAssignedList) {
   void loadRbmStateTableData()
  }
 }, [levelConfig.autoLoadAssignedList, props.userRole])

 useEffect(() => {
  handleSearch(searchTerm)
 }, [searchTerm])

 return (
  <>
   <div className='w-full'>
    <div className='text-[#171B1F] text-lg font-normal pb-6'>
     <div>
      {t('userManagement.activate.geoMapping.title')}
     </div>
    </div>

    <form
     method='POST'
     action=''
     onSubmit={handleSubmit(submitFormData)}
    >
     <div className='grid md:grid-cols-3 gap-5 w-full mt-4 mb-10 text-[#171B1F]'>
      {levelConfig.showStateDropdown && (
       <Select2DropdownField
        label={t(
         'userManagement.activate.geoMapping.state'
        )}
        labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
        className={`${
         isDisabled
          ? '!text-[#33393D] font-medium '
          : 'font-normal !text-[#33393D]'
        } boxed-select2 relative block text-lg border-0  !rounded-l-lg`}
        wrapperClassName='relative w-full'
        inputWrapperClassName='pl-4 py-2 px-4 '
        required={true}
        disabled={false}
        isClearable={false}
        isSearchable={true}
        control={control}
        customStyle={{
         transition: 'unset',
         boxShadow: 'unset',
         border: '0px',
         borderRadius: '0px',
         padding: '0px 0px 0px 0px',
         minHeight: 'auto',
        }}
        data={stateList}
        rules={{
         validate: {
          required: (v: string) => {
           if (v === '')
            return `${t('helperText.plsSelect')} ${t(
             'userManagement.activate.geoMapping.state'
            )}`
          },
         },
         onChange: (e: {target: {value: number}}) => {
          void getDistrict(e.target.value)
         },
        }}
        id={`state`}
        name={`state`}
        OptionValueKey='id'
        OptionLabelKey='name'
        placeholder={`${t('helperText.select')} ${t(
         'userManagement.activate.geoMapping.state'
        )}`}
        errors={errors}
       />
      )}

      {levelConfig.showDistrictDropdown && (
       <Select2DropdownField
        label={t(
         'userManagement.activate.geoMapping.district'
        )}
        labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
        className={`${
         isDisabled
          ? '!text-[#33393D] font-medium '
          : 'font-normal !text-[#33393D]'
        } boxed-select2 relative block text-lg border-0  !rounded-l-lg`}
        wrapperClassName='relative w-full'
        inputWrapperClassName='pl-4 py-2 px-4 '
        required={true}
        disabled={false}
        isClearable={false}
        isSearchable={true}
        control={control}
        customStyle={{
         transition: 'unset',
         boxShadow: 'unset',
         border: '0px',
         borderRadius: '0px',
         padding: '0px 0px 0px 0px',
         minHeight: 'auto',
        }}
        data={districtList}
        rules={{
         validate: {
          required: (v: string) => {
           if (v === '')
            return `${t('helperText.plsSelect')} ${t(
             'userManagement.activate.geoMapping.district'
            )}`
          },
         },
         onChange: () => {
          setTableDataList([])
         },
        }}
        id={`district`}
        name={`district`}
        OptionValueKey='id'
        OptionLabelKey='name'
        placeholder={`${t('helperText.select')} ${t(
         'userManagement.activate.geoMapping.district'
        )}`}
        errors={errors}
       />
      )}
      {levelConfig.showLoadButton && (
       <Button
        type='button'
        disabled={
         !watch(
          level === 'subDistrict' ? 'district' : 'state'
         )
        }
        onClick={loadTableData}
        color='blue'
        className='my-2 text-center py-[9px] px-6 bg-[#4E83E4] 
                  !rounded-lg text-base text-white font-semibold border-[#4E83E4] disabled:!text-[#C5CED6] disabled:hover:!text-[#C5CED6] disabled:!bg-white'
       >
        {level === 'subDistrict'
         ? t(
            'userManagement.activate.geoMapping.loadSubDistrict'
           )
         : t(
            'userManagement.activate.geoMapping.loadDistrict'
           )}
       </Button>
      )}
     </div>
     {tableDataList.length > 0 && (
      <div className='w-full'>
       <div className='text-[#171B1F] text-lg font-normal'>
        {getLevelLabel()}
       </div>
       <KTSearchBox
        label={t('userManagement.activate.search')}
        placeholder={getLevelLabel()}
        wrapperClass='mt-6 !p-3'
        setSearchTerm={setSearchTerm}
       />
       <div className='w-full mt-6'>
        <KTTable
         data={tableRows}
         columns={tableColList}
         headerClass='bg-[#80868C]'
         headerColClass='!border-0 text-left'
         colClass='text-[#33393D] !text-sm font-normal !border-0'
         tableClass=''
         rowClass='even:bg-[#E6EBF0]'
         showPagination={false}
        />
       </div>
      </div>
     )}
     {(tableDataList?.length > 0 || level === 'state') && (
      <div className='w-full border-t border-[#E6EBF0] bg-white py-6'>
       <div className='flex items-center justify-end pr-[72px]'>
        <Button
         type='submit'
         disabled={false}
         color='blue'
         className='text-center py-[9px] px-6 bg-[#4E83E4] 
                  !rounded-lg text-base text-white font-semibold border-[#4E83E4]'
        >
         {t('userManagement.activate.saveNNext')}
        </Button>
       </div>
      </div>
     )}
    </form>
   </div>
  </>
 )
}

export default GeographyMapping
